import React, { useContext, useEffect, useRef } from 'react';

import { AnimatePresence, useAnimation } from 'framer-motion';
import { useParams } from 'react-router-dom';
import { ThemeContext } from 'styled-components';

// import { publish, useTopic } from '@pkg/components/Sections/Profile/pubSub';
// import { glRemoveFace, glShowUIButtons } from '@pkg/components/Sections/Profile/pubSub/constants';
import { useTopic } from '@pkg/components/Sections/Profile/pubSub';
import { glShowUIButtons } from '@pkg/components/Sections/Profile/pubSub/constants';
import { useAppSelector } from '@pkg/hooks';
import { useMediaQuery } from '@pkg/providers';

import { PathParams } from '@app/routes/types';
//import { setYatPageLiquidID } from '@app/store/slices/yat-page';
import { LiquidIDEditStage } from '@app/store/slices/yat-page/types';
import { type NFTItem } from '@app/store/types/nft-manager';

import LiquidIDItemDetails from './LiquidIDItemDetails';
//import LiquidIDThumbnails from './LiquidIDThumbnails';

import { EditButtonsGroup, LayoutSplitter, LiquidIDPosition, LiquidIDSectionWrapper, NavigationBar } from './styles';

export interface LiquidIDSectionProps {
    editMode?: boolean;
}

export function LiquidIDSection({ editMode = false }: LiquidIDSectionProps) {
    // const dispatch = useAppDispatch();
    const liquidIDItems = useAppSelector((state) => state.yatPage.liquidIDItems);
    const liquidIDEditStage = useAppSelector((state) => state.yatPage.liquidIDEditStage);
    const liquidIDExpandedPreview = useAppSelector((state) => state.yatPage.liquidIDExpandedPreview);
    const liquidIDLoading = useAppSelector((state) => state.yatPage.liquidIDLoading);
    const editingLiquidID = useAppSelector((state) => state.yatPage.editStates.editingLiquidID);

    const theme = useContext(ThemeContext);
    const { tablet } = useMediaQuery();
    const initialItemsRef = useRef<(NFTItem | undefined)[]>([]);
    const hasInitialRef = useRef(false);
    const uiControls = useAnimation();

    const { [PathParams.Yat]: yat } = useParams();
    const primaryYat = useAppSelector((state) => state.user?.primaryYat);
    const isCurrentUserProfile = primaryYat == yat;

    useTopic(glShowUIButtons, (value) => {
        // Controls display of thumbnails when transitioning between editor/preview + collapsed
        if (uiControls) {
            const reveal = value && ((liquidIDExpandedPreview && !tablet) || editingLiquidID);
            void uiControls.start(reveal ? 'visible' : 'hidden');
        }
    });

    useEffect(() => {
        if (liquidIDLoading) {
            initialItemsRef.current = [];
            hasInitialRef.current = false;
            return;
        }

        if (liquidIDEditStage === LiquidIDEditStage.Selection) {
            if (!hasInitialRef.current) {
                // to handle the cancel
                initialItemsRef.current = liquidIDItems;
                hasInitialRef.current = true;
            }
        } else {
            initialItemsRef.current = [];
            hasInitialRef.current = false;
        }
    }, [liquidIDEditStage, liquidIDItems, liquidIDLoading]);

    // const removeFace = useCallback(
    //     (face: number) => {
    //         const currentItems = [...liquidIDItems];
    //         currentItems[face] = undefined;
    //
    //         dispatch(setYatPageLiquidID({ items: currentItems }));
    //
    //         publish(glRemoveFace, currentItems);
    //     },
    //     [liquidIDItems, dispatch],
    // );

    const sharedTransitionProps = {
        duration: theme.transitions.speeds.fast,
        ease: theme.transitions.eases.on,
    };

    return (
        <AnimatePresence>
            {(editMode && editingLiquidID) || liquidIDExpandedPreview ? (
                <LayoutSplitter
                    key="layout-splitter"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                >
                    <LiquidIDSectionWrapper
                        key="liquid-id-section-wrapper"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={sharedTransitionProps}
                    >
                        <LiquidIDPosition editMode={editMode} />
                        <EditButtonsGroup
                            key="thumbnailsWrapper"
                            initial={'hidden'}
                            variants={{
                                hidden: {
                                    opacity: 0,
                                },
                                visible: {
                                    opacity: 1,
                                },
                            }}
                            animate={uiControls}
                            transition={{
                                duration: theme.transitions.speeds.normal,
                                ease: theme.transitions.eases.cubic,
                            }}
                        >
                            {
                                // <LiquidIDThumbnails removeFace={removeFace} />
                            }
                        </EditButtonsGroup>
                    </LiquidIDSectionWrapper>
                    <LiquidIDItemDetails expanded />
                </LayoutSplitter>
            ) : null}
            <NavigationBar
                $editor={editingLiquidID}
                $userLIDPreview={liquidIDExpandedPreview && !editingLiquidID && isCurrentUserProfile}
            ></NavigationBar>
        </AnimatePresence>
    );
}
